import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid
} from "recharts";
import { Receipt, Package, CurrencyInr, Clock, ArrowRight, Crown } from "@phosphor-icons/react";

export default function ShopDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/shopkeeper").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-6">Loading dashboard…</div>;

  const { stats, top_products, daily_sales, recent_orders, store, subscription } = data;
  const limitUsed = subscription.product_limit === -1 ? 0
    : Math.round((subscription.products_used / subscription.product_limit) * 100);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 fade-up">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Hey, {store.name} 👋</h1>
          <p className="text-gray-600 mt-1 text-sm">Here's what's happening today.</p>
        </div>
        <Link to="/admin/products/new" className="qd-btn-primary inline-flex items-center gap-2" data-testid="dash-add-product-btn">
          + Add product with AI
        </Link>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Tile icon={Receipt} label="Total orders" value={stats.total_orders} color="#F8CB46" testId="tile-total-orders" />
        <Tile icon={Clock} label="Pending" value={stats.pending} color="#F59E0B" testId="tile-pending" />
        <Tile icon={CurrencyInr} label="Revenue" value={`₹${Math.round(stats.revenue).toLocaleString("en-IN")}`} color="#15803D" testId="tile-revenue" />
        <Tile icon={Package} label="Products" value={stats.products} color="#3B82F6" testId="tile-products" />
      </div>

      {/* Subscription banner */}
      <div className="qd-card p-5 flex flex-wrap items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#F8CB46]/20 flex items-center justify-center">
          <Crown weight="fill" size={22} className="text-[#15803D]" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500">Current plan</div>
          <div className="font-heading font-black text-xl capitalize">{subscription.plan}</div>
          <div className="text-xs text-gray-500">Status: <span className="font-semibold text-green-600 capitalize">{subscription.status}</span></div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs text-gray-500 mb-1">
            Products: {subscription.products_used} / {subscription.product_limit === -1 ? "∞" : subscription.product_limit}
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#15803D]" style={{ width: `${Math.min(limitUsed, 100)}%` }} />
          </div>
        </div>
        <Link to="/admin/subscription" className="qd-btn-primary text-sm inline-flex items-center gap-1" data-testid="dash-manage-sub">
          Manage <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="qd-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-gray-500">Last 7 days</div>
              <h3 className="font-heading text-xl font-bold">Daily revenue</h3>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={daily_sales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F1F4" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} style={{ fontSize: 12 }} />
                <YAxis tickLine={false} axisLine={false} style={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: "#F8CB4622" }} formatter={(v) => `₹${v}`} />
                <Bar dataKey="revenue" fill="#F8CB46" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="qd-card p-5">
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500">Top selling</div>
          <h3 className="font-heading text-xl font-bold mb-4">Best products</h3>
          {top_products.length === 0 ? (
            <div className="text-sm text-gray-500">No sales yet</div>
          ) : (
            <div className="space-y-3">
              {top_products.map((p, i) => (
                <div key={p.id} className="flex items-center gap-3" data-testid={`top-product-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden">
                    {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.sold} sold</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent orders */}
      <div className="qd-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-gray-500">Recent</div>
            <h3 className="font-heading text-xl font-bold">Latest orders</h3>
          </div>
          <Link to="/admin/orders" className="text-sm font-semibold text-[#15803D]" data-testid="dash-view-orders">View all →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="py-2 font-semibold">Order</th>
                <th className="py-2 font-semibold hidden sm:table-cell">Customer</th>
                <th className="py-2 font-semibold">Total</th>
                <th className="py-2 font-semibold">Status</th>
                <th className="py-2 font-semibold hidden md:table-cell">Mode</th>
              </tr>
            </thead>
            <tbody>
              {recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-gray-50">
                  <td className="py-3 font-mono text-xs">{o.order_number}</td>
                  <td className="py-3 hidden sm:table-cell">{o.customer_name}</td>
                  <td className="py-3 font-semibold">₹{o.total}</td>
                  <td className="py-3"><StatusPill status={o.status} /></td>
                  <td className="py-3 hidden md:table-cell capitalize">{o.delivery_mode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="qd-card p-4" data-testid={testId}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${color}22` }}>
        <Icon weight="duotone" size={20} style={{ color }} />
      </div>
      <div className="text-xs text-gray-500 uppercase tracking-wide font-bold">{label}</div>
      <div className="font-heading font-black text-2xl">{value}</div>
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    new: "bg-blue-50 text-blue-700",
    accepted: "bg-amber-50 text-amber-700",
    packed: "bg-purple-50 text-purple-700",
    out_for_delivery: "bg-orange-50 text-orange-700",
    out_for_self_delivery: "bg-orange-50 text-orange-700",
    delivered: "bg-green-50 text-green-700",
    cancelled: "bg-red-50 text-red-700",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-bold capitalize ${map[status] || "bg-gray-100"}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}
